import { css } from 'styled-components';

import { floorGameDuration as duration, floorGameEasing as easing, styledReset } from '@pkg/primitives';

///
/// Wrapper and table

// See these CodePen demoes for more insight on the complex `maskStyles`:
// Rounded corner masking: https://codepen.io/beefchimi/pen/vYQrEde
// Sticky table elements: https://codepen.io/beefchimi/pen/poQVWWN

// prettier-ignore
export const maskStyles = css`
    mask-image:
        // Slim rect to cover the <thead />
        linear-gradient(
            to bottom,
            black var(--data-table-thead-height),
            transparent var(--data-table-thead-height)
        ),
        // Large rect to cover top-to-bottom (inset from left/right)
        linear-gradient(
            to bottom,
            transparent var(--mask-vert-rect-start),
            black var(--mask-vert-rect-start),
            black calc(100% - var(--mask-radius)),
            transparent calc(100% - var(--mask-radius))
        ),
        // Large rect to cover left-to-right (inset from top/bottom)
        linear-gradient(
            to right,
            transparent var(--mask-radius),
            black var(--mask-radius),
            black calc(100% - var(--mask-radius)),
            transparent calc(100% - var(--mask-radius))
        ),
        // Circles for each corner
        radial-gradient(circle, black var(--mask-radius), transparent var(--mask-radius-blur)),
        radial-gradient(circle, black var(--mask-radius), transparent var(--mask-radius-blur)),
        radial-gradient(circle, black var(--mask-radius), transparent var(--mask-radius-blur)),
        radial-gradient(circle, black var(--mask-radius), transparent var(--mask-radius-blur));
    mask-size:
        cover,
        cover,
        cover,
        var(--mask-circle-size) var(--mask-circle-size),
        var(--mask-circle-size) var(--mask-circle-size),
        var(--mask-circle-size) var(--mask-circle-size),
        var(--mask-circle-size) var(--mask-circle-size);
    mask-position:
        center,
        center,
        center var(--data-table-thead-height),
        left var(--data-table-thead-height),
        right var(--data-table-thead-height),
        bottom right,
        bottom left;
    mask-repeat: no-repeat;
`;

///
/// All cells

const cellScrollFade = css`
    // NOTE: These styles are hopefully temporary until
    // we can figure out a better scrollable table implementation.
    // The number of columns chosen to be "fadable" is arbitrary.
    transition: opacity ${duration.normal}ms linear;

    &:nth-child(2) {
        opacity: var(--cell-opacity-col-2, 1);
    }

    &:nth-child(3) {
        opacity: var(--cell-opacity-col-3, 1);
    }

    &:nth-child(4) {
        opacity: var(--cell-opacity-col-4, 1);
    }

    &:nth-child(5) {
        opacity: var(--cell-opacity-col-5, 1);
    }

    &:nth-child(6) {
        opacity: var(--cell-opacity-col-6, 1);
    }
`;

export const cellBase = css`
    ${styledReset.tableCell};
    ${cellScrollFade};

    padding: var(--data-table-cell-padding);
    white-space: nowrap;

    div[data-table-align='end'] &:last-child {
        --data-table-last-cell-align: end;
        text-align: right;
    }

    div[data-table-collapse='collapse'] & {
        // "end" is required so that we snap to the right-edge of the
        // scroll container. Since we have sticky elements that are dynamic,
        // we cannot use 'scroll-padding' to predict an offset.
        scroll-snap-align: end;

        &:first-child {
            padding-right: var(--data-table-cell-padding-big);
        }
    }
`;

///
/// Tbody cells

export const bodyCellBase = css`
    ${cellBase};
    justify-content: var(--data-table-last-cell-align);

    div[data-table-collapse='separate'] & {
        background-color: var(--data-table-bg-color);
        transition-property: background-color;
        transition-timing-function: ${easing.cubic};
    }

    div[data-table-collapse='separate'] tbody tr:hover & {
        background-color: var(--data-table-highlight-color);
    }
`;

export const firstCellCorners = css`
    border-top-left-radius: var(--data-table-cell-radius-top-left);
    border-bottom-left-radius: var(--data-table-cell-radius-bottom-left);
`;
